import React, { useState } from 'react';
import { DndProvider } from 'react-dnd';
import { DragHandle, DropArea } from '../../../common/DragNDrop.jsx';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';
import { useSetWidgetAttribute } from '../../hooks/useSetWidgetAttribute.js';
import { useTaskTitleList } from '../../hooks/useTaskTitleList.js';
import LoadingIndicator from '../../../common/loading_indicator.jsx';
import Backend from 'react-dnd-html5-backend';
import TaskStatusWidgetSettingsRow from './TaskStatusWidgetSettingsRow.jsx';
import { closeDetail, openDetail, saveWidgetSettings } from '../../redux/dashboardSlice.js';
import { FaIcon } from '../../../common/Icons.jsx';
import styles from '../../settings_sidebar/common/ListGroupItem.module.scss';
import CollapsibleSection from '../../../common/CollapsibleSection.jsx';
import { MIN_FONT, MAX_FONT, IMAGE_DEFAULT_FONT, VALUE_DEFAULT_FONT, NAME_ITEM_DEFAULT_FONT } from '../../settings_sidebar/chart/ChartSettings.jsx';
import commonStyles from '../base/CommonSettings.module.scss';
import Select from '../../../common/Select.jsx';
import ToggleSwitch from '../../../common/ToggleSwitch.jsx';
import DetailSidebarSettings, { SIDEBAR_SETTING_TYPES } from '../../settings_sidebar/DetailSidebarSettings.jsx';
import CommonSlider from '../../../common/CommonSlider.jsx';

const DND_ITEM_TYPE = "report-variable-row";
const HOVER_THRESHOLD = 0.25;

function TaskStatusWidgetSettings({ widgetId }) {
    const dispatch = useDispatch();
    const setAttribute = useSetWidgetAttribute(widgetId);
    const variables = useSelector(state => state.dashboard.widgets[widgetId].settings?.variables);
    const imageFontSize = useSelector(state => state.dashboard.widgets[widgetId].settings?.image_fontSize);
    const valueFontSize = useSelector(state => state.dashboard.widgets[widgetId].settings?.value_fontSize);
    const nameItemFontSize = useSelector(state => state.dashboard.widgets[widgetId].settings?.name_item_fontSize);
    const stacking = useSelector(state => state.dashboard.widgets[widgetId].settings?.stacking);
    const projectFilterEnabled = useSelector(state => state.dashboard.widgets[widgetId].settings?.projectFilterEnabled ?? false);
    const colorScheme = useSelector(state => state.dashboard.colorScheme);
    const {data: settingsVariables, isLoading: loading, error: loadError} = useTaskTitleList();
    const [variableIndex, setVariableIndex] = useState(null);
    
    const toggleDetail = (index) => {
        if (index != null) {
            dispatch(openDetail(`${SIDEBAR_SETTING_TYPES.task_status}-${index}`));
        } else {
            dispatch(closeDetail());
        }
        setVariableIndex(index);
    }
    
    const saveSettings = () => {
        dispatch(saveWidgetSettings());
        toggleDetail(null);
    }

    const onClose = () => {
        setVariableIndex(null);
    }

    const updateVariableSetting = (key, index, value) => {
        let currentVar = cloneDeep(variables);
        let variable = {...currentVar[index]};
        variable[key] = value;
        currentVar[index] = variable;
        setAttribute('settings.variables', currentVar)
    }

    const addVariable = () => {
        const newVar = {
            report_variable_id: '',
            unit: '',
            name_item: '',
            quantity: '',
            image: '',
            background_color: colorScheme[(variables.length%colorScheme.length)],
            text_color: '#ffffff',
            task_title: '',
            id: StringHelper.random(8)
        };
        setAttribute('settings.variables', [...variables, newVar])
    }

    const removeVariable = (index) => {
        const currentVar = cloneDeep(variables);
        if (currentVar.length > 1){
            currentVar.pop();
            setAttribute('settings.variables', currentVar)
        }
    }

    const moveVariable = (draggedIndex, targetIndex) => {
        const currentVar = cloneDeep(variables);
        const [v] = currentVar.splice(draggedIndex, 1);
        currentVar.splice(targetIndex, 0, v);
        setAttribute('settings.variables', currentVar);
    }

    const renderVariableSettings = () => {
        let currentVariables = [];

        variables.forEach(
            function (v, index) {
            currentVariables.push(
                <DropArea
                key={v.id}
                accept={DND_ITEM_TYPE}
                item={{ id: v.id, index, type: DND_ITEM_TYPE }}
                onMove={moveVariable}
                hoverThreshold={HOVER_THRESHOLD}
                render={({ dropRef, dragRef, isDragging }) => (
                    <li ref={dropRef} style={{opacity: isDragging ? 0.5 : 1}} className={`widget-container list-group-item ${styles['list-group-item']}`}>
                        <DragHandle sortHandleClass={styles['sort-handle']} dragRef={dragRef} />
                        <div className={styles['list-group-item-content']}>
                            <div className={styles['list-group-item-description']}>
                                <h4 className='text-truncate'>{I18n.t('task')} {v.id}</h4>
                                <p className='text-truncate'>{v?.name_item || I18n.t('unnamed')}</p>
                            </div>
                        </div>
                        <div className={styles['list-group-item-actions']} style={{justifyContent: 'flex-end'}}>
                            <a onClick={() => toggleDetail(index)}>
                                <FaIcon type='cog' style={{color: 'black'}} />
                            </a>
                        </div>
                    </li>
                )}
                />
            );
        }); 

        return <div className='list-group mt-1'>
            <DndProvider backend={Backend}>{currentVariables}</DndProvider>   
        </div>
    }

    return (
        loading ? <LoadingIndicator additionalInfo={loadError ? 'Failed to load configurations' : ''} /> : <>
        <hr />
        <CollapsibleSection title={I18n.t('task_details')}>
            { variableIndex != null &&
                <DetailSidebarSettings onClose={onClose} saveSettings={saveSettings} sidebarId={`${SIDEBAR_SETTING_TYPES.task_status}-${variableIndex}`}>
                    <h4>{`Edit ${variables[variableIndex]?.name_item || I18n.t('unnamed')}`}</h4>
                    <TaskStatusWidgetSettingsRow
                        {...({variables, index: variableIndex, updateVariableSetting, settingsVariables})}
                    />
                </DetailSidebarSettings>
            }
            <div className={`${commonStyles['settings-section']} flex-dir-row justify-content-space-between mb-1`}>
                <label>{I18n.t('project_filter_enabled')}</label>
                <ToggleSwitch
                    name='project_filter_enabled'
                    isOn={projectFilterEnabled}
                    defaultIsOn={projectFilterEnabled}
                    handleToggle={() => setAttribute('settings.projectFilterEnabled', !projectFilterEnabled)}
                />
            </div>
            <div className={commonStyles['settings-option']}>
                <label className={styles['settings-option']} style={{marginBottom: '4px'}}>
                    {I18n.t('stacking_direction')}
                    <Select
                        options={[{label: I18n.t('vertical'), value: 'vertical'}, {label: I18n.t('horizontal'), value: 'horizontal'}]}
                        value={stacking || 'horizontal'}
                        placeholder={I18n.t('select_from_list')}
                        onChange={(v) => setAttribute('settings.stacking', v)}
                    />
                </label>
            </div>
            <div className={commonStyles['settings-option']}>
                <label>{`${I18n.t('image')} ${I18n.t('normalize_font_size')}`}</label>
                <CommonSlider 
                    value={imageFontSize || IMAGE_DEFAULT_FONT} 
                    min={MIN_FONT} 
                    max={MAX_FONT+100} 
                    onChange={(value) => setAttribute('settings.image_fontSize', value)}
                />
            </div>
            <div className={commonStyles['settings-option']}>
                <label>{`${I18n.t('value')} ${I18n.t('normalize_font_size')}`}</label>
                <CommonSlider 
                    value={valueFontSize || VALUE_DEFAULT_FONT} 
                    min={MIN_FONT} 
                    max={MAX_FONT} 
                    onChange={(value) => setAttribute('settings.value_fontSize', value)}
                />
            </div>
            <div className={commonStyles['settings-option']}>
                <label>{`${I18n.t('name_item')} ${I18n.t('normalize_font_size')}`}</label>
                <CommonSlider 
                    value={nameItemFontSize || NAME_ITEM_DEFAULT_FONT} 
                    min={MIN_FONT} 
                    max={MAX_FONT} 
                    onChange={(value) => setAttribute('settings.name_item_fontSize', value)}
                />
            </div>
            {renderVariableSettings()}
            <fieldset>
                <button className="btn btn-primary" onClick={addVariable}>{I18n.t('add_variable')}</button>
                <button className="btn btn-danger" onClick={removeVariable}>{I18n.t('remove_variable')}</button>
            </fieldset>
        </CollapsibleSection>
    </>)
}

export default TaskStatusWidgetSettings;
